import React, { useEffect, useState } from 'react';
import { Link, useParams, useHistory } from 'react-router-dom';
import { Button, Table } from 'reactstrap';
import { useGetScanTunnelQuery } from '../../../graphql/queries/getScanTunnel.generated';
import { useGetScanTunnelSessionsQuery } from '../../../graphql/queries/getScantunnelSessions.generated';
import { useStartScanTunnelSessionMutation } from '../../../graphql/mutations/startScanTunnelSession.generated';
import { Communicator } from '../../../tools/Communication';
import { DEFAULT_CONTAINER_TYPE_ICON } from '@/utils/imageUrl';
import { ContentSection } from '../../../components/Layout/ContentSection';
import { Routes } from '@/utils/routes';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

export const ScanTunnelDetailPage = () => {
  const { scanTunnelId } = useParams<{ scanTunnelId: string }>();
  const history = useHistory();

  const {
    data: scanTunnelData,
    loading: loadingScanTunnel,
    error: scanTunnelError,
  } = useGetScanTunnelQuery({
    variables: { id: scanTunnelId },
  });

  useDocumentTitle('PageTitles:scanTunnel', scanTunnelData?.scanTunnel?.name);

  const {
    data: scanTunnelSessionsData,
    loading: loadingScanTunnelSessions,
    error: scanTunnelSessionsError,
  } = useGetScanTunnelSessionsQuery({
    variables: { scanTunnelId },
  });

  type ContainerType = {
    id: number;
    name: string;
    imageUrl: string;
  };

  const [containerTypes, setContainerTypes] = useState<ContainerType[]>([]);

  const [startScanTunnelSessionMutation] = useStartScanTunnelSessionMutation();

  const handleStartNewSession = async () => {
    const response = await startScanTunnelSessionMutation({
      variables: { scanTunnelId },
    });
    const newSessionId = response.data?.startScanTunnelSession.id;
    if (newSessionId) {
      history.push(Routes.scanTunnelSession(newSessionId));
    }
  };

  useEffect(() => {
    Communicator.Get('api/3/containers/getAllContainerTypes', {})
      .then((response) => response.json())
      .then((data) => {
        setContainerTypes(data);
      });
  }, []);

  if (loadingScanTunnel || loadingScanTunnelSessions)
    return <div>Loading...</div>;
  if (scanTunnelError) return <div>Error: {scanTunnelError.message}</div>;
  if (scanTunnelSessionsError)
    return <div>Error: {scanTunnelSessionsError.message}</div>;

  function getContainerTypeInfo() {
    const type = containerTypes.find(
      (ct) => ct.id === (scanTunnelData?.scanTunnel?.container_type_id || 1),
    );
    return (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <p style={{ marginRight: '10px' }}>Scan Tunnel Container Type:</p>
        <img
          style={{ width: 40, height: 40, marginRight: '10px' }}
          src={type?.imageUrl || DEFAULT_CONTAINER_TYPE_ICON}
          alt=""
        />
        {type?.id} - {type?.name}
      </div>
    );
  }

  return (
    <ContentSection
      title={`Scan Tunnel "${scanTunnelData?.scanTunnel?.name}"`}
      collapsable={false}
    >
      <p>Scan Tunnel Mode: {scanTunnelData?.scanTunnel?.mode}</p>
      <p>{getContainerTypeInfo()}</p>

      <Link to={Routes.editScanTunnel(scanTunnelId)}>
        <Button size="sm" color="primary">
          Edit
        </Button>
      </Link>

      <h2 className="mb-2 mt-4">Scan Tunnel Sessions</h2>

      <Button
        size="sm"
        color="primary"
        className="mb-2"
        onClick={handleStartNewSession}
      >
        Start a new Session
      </Button>

      <Table>
        <thead>
          <tr>
            <th>Session ID</th>
            <th>Is Active</th>
            <th>Active Until</th>
          </tr>
        </thead>
        <tbody>
          {[...(scanTunnelSessionsData?.getScanTunnelSessions ?? [])]
            .sort((a, b) => {
              if (a.is_active) {
                return -1;
              }
              if (b.is_active) {
                return 1;
              }
              if (a.active_until != null && b.active_until != null) {
                return new Date(a.active_until) > new Date(b.active_until)
                  ? -1
                  : 1;
              }
              return -1;
            })
            .map((session) => (
              <tr key={session.id}>
                <td>
                  <Link to={Routes.scanTunnelSession(session.id)}>
                    {session.id}
                  </Link>
                </td>
                <td>{session.is_active ? 'Yes' : 'No'}</td>
                <td>{session.active_until}</td>
              </tr>
            ))}
        </tbody>
      </Table>
    </ContentSection>
  );
};
